import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange } from "lucide-react";
import { CONTACTO, waLink, track } from "@/data/content";
import { useLeadSubmit } from "@/lib/useLeadSubmit";

const SERVICIOS_CAMPANA = [
  "Pulverización",
  "Siembra al voleo",
  "Fertilización",
  "Dispersión de sólidos",
  "Mapas multiespectrales",
];

const resumen = (f) =>
  [
    `Propiedad: ${f.propiedad}`,
    `Ubicación: ${f.ubicacion}`,
    `Hectáreas: ${f.hectareas}`,
    `Cultivo: ${f.cultivo}`,
    `Servicios de la campaña: ${f.servicios.join(", ")}`,
    `Período: del ${f.inicio} al ${f.fin}`,
    `Comentario: ${f.comentario || "—"}`,
  ].join("\n");

export default function AgendarCampana() {
  const { sending, ok, error, submit, reset } = useLeadSubmit();
  const [f, setF] = useState({
    nombre: "", whatsapp: "", propiedad: "", ubicacion: "", hectareas: "",
    cultivo: "", inicio: "", fin: "", comentario: "", servicios: [], website: "",
  });

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleServicio = (s) =>
    setF((st) => ({
      ...st,
      servicios: st.servicios.includes(s) ? st.servicios.filter((x) => x !== s) : [...st.servicios, s],
    }));

  // Al enviar: la solicitud ya quedó guardada y el correo enviado; se abre
  // WhatsApp con todos los datos escritos.
  useEffect(() => {
    if (ok) window.open(waLink(`Hola CERTERO SRL, quiero agendar una campaña completa:\n${resumen(f)}`), "_blank");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ok]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!f.servicios.length) return;
    track("campana_completa_submit", {});
    submit(
      {
        nombre: f.nombre,
        telefono: f.whatsapp,
        tipo: "campana",
        interes: "Campaña completa",
        mensaje: resumen(f),
        website: f.website,
        consent: true,
      },
      () => toast.success("Solicitud de campaña registrada. El equipo CERTERO lo contactará.")
    );
  };

  const inputCls =
    "h-11 w-full border border-hud bg-abyss px-3 text-sm text-mist placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-certero [color-scheme:dark]";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-smoke";

  if (ok) {
    return (
      <div data-testid="campana-success" className="border border-certero/40 bg-ink p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-certero bg-certero/10">
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-certero" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
        </span>
        <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-mist">Campaña registrada</h3>
        <p className="mt-2 max-w-lg text-sm leading-relaxed text-smoke">
          Su solicitud quedó registrada y el equipo de CERTERO la revisará. Si no se abrió
          WhatsApp, puede enviar los datos con este botón.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            data-testid="campana-success-whatsapp"
            href={waLink(`Hola CERTERO SRL, quiero agendar una campaña completa:\n${resumen(f)}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 items-center rounded-full bg-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-[#4bc443]"
          >
            Enviar por WhatsApp
          </a>
          <button
            data-testid="campana-success-reset"
            onClick={reset}
            className="inline-flex h-11 items-center rounded-full border border-hud px-6 font-mono text-[11px] uppercase tracking-[0.2em] text-mist/80 transition-colors duration-300 hover:border-certero hover:text-certero"
          >
            Agendar otra campaña
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="campana-form" onSubmit={onSubmit} className="border border-hud bg-ink p-7 sm:p-8">
      <div className="flex items-center gap-3">
        <CalendarRange className="h-5 w-5 text-certero" strokeWidth={1.5} />
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-certero">Agendar campaña</p>
      </div>
      <h3 className="mt-3 max-w-2xl font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">
        Agende toda su campaña con CERTERO y lo acompañamos de inicio a fin.
      </h3>

      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="campana-nombre">Nombre completo *</label>
          <input id="campana-nombre" data-testid="campana-input-nombre" className={inputCls} value={f.nombre} onChange={set("nombre")} required minLength={2} autoComplete="name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-whatsapp">Número de WhatsApp *</label>
          <input id="campana-whatsapp" data-testid="campana-input-whatsapp" className={inputCls} value={f.whatsapp} onChange={set("whatsapp")} required minLength={5} type="tel" placeholder="+591 ..." autoComplete="tel" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-propiedad">Propiedad *</label>
          <input id="campana-propiedad" data-testid="campana-input-propiedad" className={inputCls} value={f.propiedad} onChange={set("propiedad")} required minLength={2} placeholder="Nombre de la estancia o campo" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-ubicacion">Ubicación *</label>
          <input id="campana-ubicacion" data-testid="campana-input-ubicacion" className={inputCls} value={f.ubicacion} onChange={set("ubicacion")} required minLength={2} placeholder="Zona, municipio o coordenadas" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-hectareas">Hectáreas *</label>
          <input id="campana-hectareas" data-testid="campana-input-hectareas" className={inputCls} value={f.hectareas} onChange={set("hectareas")} required type="number" min="1" placeholder="Ej. 850" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-cultivo">Cultivo *</label>
          <input id="campana-cultivo" data-testid="campana-input-cultivo" className={inputCls} value={f.cultivo} onChange={set("cultivo")} required minLength={2} placeholder="Ej. caña de azúcar, soya, maíz" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-inicio">Inicio de la campaña *</label>
          <input id="campana-inicio" data-testid="campana-input-inicio" className={inputCls} value={f.inicio} onChange={set("inicio")} required type="date" />
        </div>
        <div>
          <label className={labelCls} htmlFor="campana-fin">Fin de la campaña *</label>
          <input id="campana-fin" data-testid="campana-input-fin" className={inputCls} value={f.fin} onChange={set("fin")} required type="date" min={f.inicio || undefined} />
        </div>
      </div>

      <fieldset className="mt-6">
        <legend className={labelCls}>Servicios de la campaña *</legend>
        <div className="flex flex-wrap gap-2" data-testid="campana-servicios">
          {SERVICIOS_CAMPANA.map((s) => {
            const on = f.servicios.includes(s);
            return (
              <button
                key={s}
                type="button"
                data-testid={`campana-servicio-${s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                onClick={() => toggleServicio(s)}
                aria-pressed={on}
                className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                  on ? "bg-certero text-ink" : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-6">
        <label className={labelCls} htmlFor="campana-comentario">Comentario (opcional)</label>
        <textarea id="campana-comentario" data-testid="campana-input-comentario" className="w-full min-h-[80px] border border-hud bg-abyss p-3 text-sm text-mist placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-certero" value={f.comentario} onChange={set("comentario")} maxLength={2000} />
      </div>

      <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      {error && (
        <p data-testid="campana-error" role="alert" className="mt-4 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid="campana-btn-submit"
        disabled={sending || !f.servicios.length}
        className="mt-6 inline-flex h-12 items-center justify-center rounded-full bg-certero px-8 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,opacity,transform] duration-300 hover:bg-[#4bc443] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {sending ? "Enviando..." : "Agendar campaña"}
      </button>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke/60">
        Al enviar, abrimos WhatsApp {CONTACTO.whatsappDisplay} con todos sus datos escritos.
      </p>
    </form>
  );
}
